(() => {
  if (customElements.get('faj-cinematic-hero')) return;

  class FajCinematicHero extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.slider = this.querySelector('[data-faj-hero-slider]');
      this.slides = Array.from(this.querySelectorAll('[data-faj-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-faj-hero-dot]'));
      this.previousButton = this.querySelector('[data-faj-previous]');
      this.nextButton = this.querySelector('[data-faj-next]');
      this.counter = this.querySelector('[data-faj-current-slide]');
      this.liveRegion = this.querySelector('[data-faj-live-region]');

      this.autoplay = this.dataset.autoplay === 'true';
      this.loop = this.dataset.loop !== 'false';
      this.draggable = this.dataset.draggable !== 'false';
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.delay = Math.max(Number.parseInt(this.dataset.autoplayDelay, 10) || 5000, 2000);
      this.transitionDuration = Math.max(Number.parseInt(this.dataset.transitionDuration, 10) || 800, 300);
      this.lazyAdjacent = Math.min(Math.max(Number.parseInt(this.dataset.lazyAdjacent, 10) || 0, 0), 2);
      this.flickityTimeout = Math.max(Number.parseInt(this.dataset.flickityTimeout, 10) || 5000, 1000);
      this.reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
      this.pauseReasons = new Set();
      this.waitTimer = null;
      this.resizeTimer = null;

      this.boundVisibility = () => this.handleVisibility();
      this.boundResize = () => this.handleResize();

      this.bindControls();
      this.updateState(0, false);

      if (!this.slider || this.slides.length < 2) {
        this.classList.add('is-static-banner');
        this.loadSlideImage(this.slides[0]);
        return;
      }

      this.waitStartedAt = performance.now();
      this.waitForFlickity();
    }

    disconnectedCallback() {
      window.clearTimeout(this.waitTimer);
      window.clearTimeout(this.resizeTimer);
      document.removeEventListener('visibilitychange', this.boundVisibility);
      window.removeEventListener('resize', this.boundResize);
      this.visibilityObserver?.disconnect();
      this.flkty?.destroy();
      this.flkty = null;
      this.initialized = false;
    }

    waitForFlickity() {
      if (typeof window.Flickity === 'function') {
        this.initializeFlickity();
        return;
      }

      if (performance.now() - this.waitStartedAt >= this.flickityTimeout) {
        this.classList.add('is-flickity-unavailable');
        this.updateState(0, false);
        console.warn('[FAJ cinematic hero] Flickity was not found. The first LCP-safe slide remains visible.');
        return;
      }

      this.waitTimer = window.setTimeout(() => this.waitForFlickity(), 50);
    }

    initializeFlickity() {
      if (this.flkty || !this.slider) return;

      this.classList.add('is-flickity-initializing');
      const physics = this.getPhysics(this.transitionDuration);

      this.flkty = new window.Flickity(this.slider, {
        accessibility: true,
        adaptiveHeight: false,
        autoPlay: this.autoplay && !this.reduceMotion ? this.delay : false,
        cellAlign: 'left',
        cellSelector: '[data-faj-hero-slide]',
        contain: !this.loop,
        draggable: this.draggable && this.slides.length > 1,
        dragThreshold: 8,
        friction: physics.friction,
        pageDots: false,
        pauseAutoPlayOnHover: this.pauseOnHover,
        prevNextButtons: false,
        resize: true,
        rightToLeft: document.documentElement.dir === 'rtl',
        selectedAttraction: physics.selectedAttraction,
        setGallerySize: false,
        wrapAround: this.loop,
        on: {
          ready: () => this.handleReady(),
          change: (index) => this.handleChange(index),
          dragStart: () => this.addPauseReason('drag'),
          dragEnd: () => this.removePauseReason('drag')
        }
      });

      if (!this.classList.contains('is-flickity-ready')) this.handleReady();
    }

    getPhysics(duration) {
      const clamped = Math.min(Math.max(duration, 400), 1400);
      const fastness = 1 - ((clamped - 400) / 1000);
      return {
        selectedAttraction: Number((0.035 + (fastness * 0.075)).toFixed(3)),
        friction: Number((0.3 + (fastness * 0.34)).toFixed(3))
      };
    }

    handleReady() {
      if (!this.flkty) return;
      this.classList.remove('is-flickity-initializing');
      this.classList.add('is-flickity-ready');
      const index = this.flkty.selectedIndex || 0;
      this.updateState(index, false);
      this.deferAdjacentLoading(index);
      this.observeVisibility();
      document.addEventListener('visibilitychange', this.boundVisibility);
      window.addEventListener('resize', this.boundResize, { passive: true });
      this.syncAutoplay();
    }

    handleChange(index) {
      this.loadAround(index);
      this.updateState(index, true);
      this.restartAutoplayAfterInteraction();
    }

    updateState(activeIndex, announce = false) {
      this.slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        this.setFocusableState(slide, active);
      });

      this.dots.forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
        dot.tabIndex = active ? 0 : -1;
      });

      if (this.counter) this.counter.textContent = String(activeIndex + 1).padStart(2, '0');

      if (announce && this.liveRegion) {
        this.liveRegion.textContent = `Slide ${activeIndex + 1} of ${this.slides.length}`;
      }
    }

    setFocusableState(slide, active) {
      slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        if (active) {
          if (element.dataset.fajPreviousTabindex !== undefined) {
            const previous = element.dataset.fajPreviousTabindex;
            previous === '' ? element.removeAttribute('tabindex') : element.setAttribute('tabindex', previous);
            delete element.dataset.fajPreviousTabindex;
          }
        } else if (element.dataset.fajPreviousTabindex === undefined) {
          element.dataset.fajPreviousTabindex = element.getAttribute('tabindex') || '';
          element.setAttribute('tabindex', '-1');
        }
      });
    }

    bindControls() {
      this.previousButton?.addEventListener('click', () => {
        this.flkty?.previous(this.loop, false);
        this.restartAutoplayAfterInteraction();
      });

      this.nextButton?.addEventListener('click', () => {
        this.flkty?.next(this.loop, false);
        this.restartAutoplayAfterInteraction();
      });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = Number.parseInt(dot.dataset.fajHeroDot, 10);
          if (!Number.isInteger(index)) return;
          this.loadAround(index);
          this.flkty?.select(index, this.loop, false);
          this.restartAutoplayAfterInteraction();
        });
      });

      this.addEventListener('focusin', () => this.addPauseReason('focus'));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.removePauseReason('focus');
      });

      if (window.Shopify?.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const blockId = event.detail?.blockId;
          const index = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
          if (index < 0) return;
          this.addPauseReason('editor');
          this.loadAround(index);
          this.flkty?.select(index, false, true);
          this.updateState(index, false);
        });

        this.addEventListener('shopify:block:deselect', () => this.removePauseReason('editor'));
      }
    }

    deferAdjacentLoading(index) {
      const firstImage = this.slides[index]?.querySelector('img');
      const schedule = () => {
        const load = () => this.loadAround(index);
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(load, { timeout: 1600 });
        } else {
          window.setTimeout(load, 220);
        }
      };

      if (!firstImage || firstImage.complete) schedule();
      else {
        firstImage.addEventListener('load', schedule, { once: true });
        firstImage.addEventListener('error', schedule, { once: true });
      }
    }

    loadAround(index) {
      if (!this.slides.length) return;
      const indexes = new Set([index]);

      for (let offset = 1; offset <= this.lazyAdjacent; offset += 1) {
        const next = index + offset;
        const previous = index - offset;

        if (this.loop) {
          indexes.add((next + this.slides.length) % this.slides.length);
          indexes.add((previous + this.slides.length) % this.slides.length);
        } else {
          if (next < this.slides.length) indexes.add(next);
          if (previous >= 0) indexes.add(previous);
        }
      }

      indexes.forEach((slideIndex) => this.loadSlideImage(this.slides[slideIndex]));
    }

    loadSlideImage(slide) {
      const picture = slide?.querySelector('[data-faj-lazy-picture]');
      if (!picture || picture.dataset.loaded === 'true') return;

      picture.querySelectorAll('source[data-srcset]').forEach((source) => {
        source.srcset = source.dataset.srcset;
        source.removeAttribute('data-srcset');
      });

      const image = picture.querySelector('img[data-src], img[data-srcset]');
      if (!image) {
        picture.dataset.loaded = 'true';
        return;
      }

      const markLoaded = () => image.classList.add('is-image-loaded');
      image.addEventListener('load', markLoaded, { once: true });
      image.addEventListener('error', markLoaded, { once: true });
      image.loading = 'eager';

      if (image.dataset.srcset) {
        image.srcset = image.dataset.srcset;
        image.removeAttribute('data-srcset');
      }
      if (image.dataset.src) {
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
      }

      picture.dataset.loaded = 'true';
      if (image.complete) markLoaded();
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window) || this.visibilityObserver) return;
      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries[0]?.isIntersecting ? this.removePauseReason('offscreen') : this.addPauseReason('offscreen');
      }, { threshold: 0.12 });
      this.visibilityObserver.observe(this);
    }

    handleVisibility() {
      document.hidden ? this.addPauseReason('document-hidden') : this.removePauseReason('document-hidden');
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => this.flkty?.resize(), 120);
    }

    addPauseReason(reason) {
      this.pauseReasons.add(reason);
      this.syncAutoplay();
    }

    removePauseReason(reason) {
      this.pauseReasons.delete(reason);
      this.syncAutoplay();
    }

    syncAutoplay() {
      if (!this.flkty || !this.autoplay || this.reduceMotion) return;
      if (this.pauseReasons.size > 0) this.flkty.pausePlayer();
      else this.flkty.unpausePlayer();
    }

    restartAutoplayAfterInteraction() {
      if (!this.flkty || !this.autoplay || this.reduceMotion || this.pauseReasons.size > 0) return;
      this.flkty.stopPlayer();
      this.flkty.playPlayer();
    }
  }

  customElements.define('faj-cinematic-hero', FajCinematicHero);
})();
